import React, { useEffect, useRef } from 'react'
import {
  DrawingTool,
  ListingsClusterManager,
  ListingsPointsManager,
  ListingsStackedsManager,
  MapboxInstance,
  PointsProvider,
  PolygonsProvider,
  StatePolygonManager,
  MunicipalityPolygonManager,
  NeighborhoodPolygonManager,
  BlockPolygonManager,
  PropertyPolygonManager,
  AutoSelectPolygonInSearch,
  CategorySelector,
  PropertiesToggleSelector
} from "@wiggot/search-components"
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider'
import { AmplitudeEvents } from '@/interfaces/enum'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import { composeClasses } from '@wiggot/utils/src/strings'

interface IProps {
  onClickHiddeCards: () => void
  onClickHiddeMap: () => void
  hiddeCards: boolean
  hiddeMap: boolean
  onResizeMap: () => void
}
export const Map = ({
  onClickHiddeCards,
  onClickHiddeMap,
  hiddeCards,
  hiddeMap,
  onResizeMap
}: IProps) => {
  const { trackEvent, isInitialized } = useAmplitude()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      onResizeMap()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [onResizeMap])

  return (
    <div
      ref={containerRef}
      className={composeClasses(
        'relative h-full flex-1 transition-all !duration-150',
        hiddeMap
          ? 'translate-x-[100vw] md:translate-x-[calc(100vw-64px)]'
          : 'sm:block md:block xl:block translate-x-0'
      )}
    >
      <button
        onClick={onClickHiddeCards}
        type="button"
        className={composeClasses(
          'absolute z-10 hidden md:flex justify-center items-center  rounded-full bg-white border border-surface-strokes p-[6px] shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)]',
          hiddeCards ? 'left-6 top-9' : '-left-4 top-2'
        )}
      >
        <GoogleIcon
          className="text-base leading-4"
          name={hiddeCards ? 'chevron_right' : 'chevron_left'}
        />
      </button>
      <MapboxInstance id="listingsMap" />
      <PointsProvider>
        <ListingsClusterManager />
        <ListingsPointsManager
          goToDetail={(id) => {
            window.open(`/search/property-detail/${id}`, '_blank')
            if (isInitialized) {
              trackEvent({
                name: AmplitudeEvents.SEARCH_RESULTS_MAPCLUSTER_ACTION
              })
            }
          }}
        />
        <ListingsStackedsManager
          goToDetail={(property) => {
            window.open(`/search/property-detail/${property?.id}`, '_blank')
            if (isInitialized) {
              trackEvent({
                name: AmplitudeEvents.SEARCH_RESULTS_MAPCLUSTER_ACTION
              })
            }
          }}
        />
      </PointsProvider>
      <PolygonsProvider>
        <StatePolygonManager />
        <MunicipalityPolygonManager />
        <NeighborhoodPolygonManager />
        <BlockPolygonManager />
        <PropertyPolygonManager />
        <AutoSelectPolygonInSearch />
      </PolygonsProvider>
      <div className="absolute flex flex-col items-end gap-2 right-6 top-6 ">
        <button
          onClick={onClickHiddeMap}
          type="button"
          className="hidden md:flex gap-2 text-sm font-semibold bg-white justify-center items-center rounded-full border border-surface-strokes shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)] p-3 h-11"
        >
          Ocultar mapa
          <GoogleIcon className="text-xl" name="map" />
        </button>
        <div className="rounded-full border border-surface-strokes tour-search-step-4 shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)]">
          <DrawingTool />
        </div>
      </div>
      <div className="flex gap-3 items-center absolute left-6 top-6">
        <div className="flex gap-2">
          <CategorySelector />
          <PropertiesToggleSelector />
        </div>
      </div>
    </div>
  )
}
